import { Component, Inject, OnDestroy } from '@angular/core';
import { CompanyGroup, CoreConfig, Subscriber, User } from '@app/models';
import { utils } from '@app/shared/utils';
import { ToastService } from '@teorema/patron';
import { FcmService } from '@app/services/fcm.service';
import { NotificationService } from '../../../../services/notification.service';
import { SubscriberService } from '../../../../services/subscriber.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'patron-notification-configuration',
  templateUrl: './notification-configuration.component.html',
  styleUrls: ['./notification-configuration.component.css'],
  standalone: false,
})
export class NotificationConfigurationComponent implements OnDestroy {
  loading: boolean = false;

  initialized = false;
  company_group?: CompanyGroup;
  bw_user?: User;
  subscriber?: Subscriber;
  topics: FormTopic[] = [
    /*new Topic({
      name: 'teste1',
      label: 'Teste1'
    }),
    new Topic({
      name: 'teste2',
      label: 'Teste2'
    })*/
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private subscriberService: SubscriberService,
    private toastService: ToastService,
    private notificationService: NotificationService,
    private fcmService: FcmService,
    @Inject('config') private coreConfig?: CoreConfig
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async init() {
    if (!this.loading && !this.initialized) {
      this.loading = true;
      this.company_group = utils.getCompanyGroup();
      this.bw_user = utils.getUser();
      this.createAvailableTopics();
      let subscriber = await this.subscriberService.getCurrentSubscriber(true);
      this.initializeSubscriber(subscriber);
    }
  }

  createAvailableTopics() {
    if (utils.checkAccess([], true).result) {
      // Aqui dentro vão os tópicos que apenas podem ser acessados por usuários (company users)
      this.topics.push(
        new FormTopic({
          name: 'new_orders',
          label: 'Novos pedidos',
        })
      );
    }
  }

  initializeSubscriber(subscriber?: Subscriber) {
    if (subscriber) {
      this.subscriber = subscriber;
    } else {
      this.subscriber = new Subscriber();
      this.subscriber.company_group_id = this.company_group!.id;
      this.subscriber.bw_user_id = this.bw_user!.id;
      this.subscriber.type = 'firebase';
    }

    if (!this.subscriber!.topics) {
      this.subscriber!.topics = [];
    } else {
      this.subscriber!.topics.forEach((topic: any) => {
        if (topic.name) {
          let topicForm = this.topics.find(tf => tf.name === topic.name);

          if (topicForm) {
            topicForm.active = true;
          }
        }
      });
    }

    this.loading = false;
    this.initialized = true;
  }

  async activateNotifications() {
    try {
      this.loading = true;
      const token = await this.fcmService.getToken();

      if (token) {
        // Create or update subscriber
        if (!this.subscriber) {
          this.subscriber = new Subscriber();
          this.subscriber.company_group_id = this.company_group!.id;
          this.subscriber.bw_user_id = this.bw_user!.id;
          this.subscriber.type = 'firebase';
          this.subscriber.value = token; // Store token in the value field
        } else {
          this.subscriber.value = token; // Update token if subscriber exists
        }

        // Save/update subscriber in the database
        if (this.subscriber.id) {
          await this.subscriberService.update(this.subscriber);
        } else {
          const result: any = await this.subscriberService.store(this.subscriber);
          if (result && result.data) {
            this.subscriber = result.data;
          }
        }

        this.toastService.show({
          color: 'success' as any,
          message: 'Notificações ativadas com sucesso!',
        });
      } else {
        this.toastService.show({
          color: 'warn' as any,
          message:
            'Não foi possível obter o token de notificação. Verifique as permissões do navegador.',
        });
      }
    } catch (e) {
      console.error('Error activating notifications:', e);
      this.toastService.show({
        color: 'error' as any,
        message: 'Não foi possível ativar as notificações. Tente novamente mais tarde.',
      });
    } finally {
      this.loading = false;
    }
  }

  async deactivateNotifications() {
    try {
      this.loading = true;

      // Delete the FCM token
      await this.fcmService.deleteToken();

      if (this.subscriber?.id) {
        // Delete the subscriber from the database
        await this.subscriberService.destroy(this.subscriber.id);
      }

      // Reset subscriber state
      this.subscriber = undefined;
      this.initialized = false;

      // Reinitialize to get clean state
      await this.init();

      this.toastService.show({
        color: 'success' as any,
        message: 'Notificações desativadas com sucesso!',
      });
    } catch (e) {
      console.error('Error deactivating notifications:', e);
      this.toastService.show({
        color: 'error' as any,
        message: 'Houve um erro ao desativar as notificações. Tente novamente mais tarde.',
      });
    } finally {
      this.loading = false;
    }
  }

  async topicsChanged(topic: FormTopic) {
    if (!this.subscriber) return;

    // Ensure topics is initialized
    if (!this.subscriber.topics) {
      this.subscriber.topics = [];
    }

    // Convert topics to string array if needed
    const topics = this.subscriber.topics.map(t =>
      typeof t === 'string' ? t : (t as any).name || ''
    );

    const topicIndex = topics.indexOf(topic.name!);
    let changed = false;

    if (topic.active && topicIndex === -1) {
      // Add topic if active and not already in the list
      topics.push(topic.name!);
      changed = true;
    } else if (!topic.active && topicIndex !== -1) {
      // Remove topic if not active and exists in the list
      topics.splice(topicIndex, 1);
      changed = true;
    }

    if (changed) {
      try {
        this.loading = true;
        // Update the topics in the subscriber
        this.subscriber.topics = topics;

        // Save the updated subscriber
        await this.subscriberService.update(this.subscriber);

        this.toastService.show({
          color: 'success' as any,
          message: 'Tópicos salvos com sucesso',
        });

        // Notify about the change - pass the topics array
        this.notificationService.topicsChangedEventEmitter.emit(this.subscriber.topics || []);
      } catch (err) {
        console.error('Error updating topics:', err);
        this.toastService.show({
          color: 'error' as any,
          message: 'Ocorreu um erro ao salvar os tópicos',
        });
      } finally {
        this.loading = false;
      }
    }
  }
}

class FormTopic {
  name?: string;
  label?: string;
  active?: boolean;

  constructor(topic?: FormTopic) {
    this.name = topic?.name;
    this.label = topic?.label;
    this.active = topic?.active;
  }
}
